import React, { useRef } from 'react';
import { Box, Card, Typography, makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { AD } from './adsAgentTokens';
import { generationLabel } from './generationStatus';

// Port of whatsapp-console-frontend `src/pages/AdTemplates/AdVariantSkeleton.js`.

const MEDIA_H = 400;
const ctaTint = fade(AD.cta, 0.12);

const useStyles = makeStyles(() => ({
  '@keyframes adSheen': {
    '0%': { transform: 'translateX(-70%) skewX(-12deg)' },
    '100%': { transform: 'translateX(220%) skewX(-12deg)' },
  },
  '@keyframes adTwinkle': {
    '0%, 100%': { opacity: 0.2, transform: 'scale(0.7)' },
    '50%': { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes adFloat': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    boxShadow: AD.shadowCard,
    borderRadius: AD.radiusLg,
    overflow: 'hidden',
    background: AD.surface,
    fontFamily: AD.fontFamily,
    // Prefix match: inside the nested ads-agent theme MUI suffixes class names.
    '& [class*="MuiSkeleton-root"]': { backgroundColor: fade(AD.primary, 0.07) },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 2px 6px 8px',
    borderBottom: `1px solid ${AD.border}`,
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  previewPane: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 12px 0',
    boxSizing: 'border-box',
  },
  caption: { marginBottom: 10, flexShrink: 0 },
  media: {
    position: 'relative',
    width: '100%',
    flex: '1 1 auto',
    minHeight: MEDIA_H,
    overflow: 'hidden',
    background: `linear-gradient(150deg, ${ctaTint} 0%, ${AD.surfaceSubtle} 55%, ${ctaTint} 100%)`,
  },
  sheen: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '45%',
    background: `linear-gradient(90deg, transparent, ${AD.surface}, transparent)`,
    opacity: 0.55,
    animation: '$adSheen 2.4s ease-in-out infinite',
  },
  stage: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    padding: '0 22px',
    textAlign: 'center',
  },
  sparkles: {
    position: 'relative',
    width: 76,
    height: 60,
    color: AD.cta,
    animation: '$adFloat 3.4s ease-in-out infinite',
  },
  star: {
    position: 'absolute',
    display: 'block',
    animation: '$adTwinkle 1.9s ease-in-out infinite',
  },
  starLead: { top: 8, left: 22, color: AD.cta },
  starUp: { top: 0, left: 4, color: AD.decorSoft, animationDelay: '0.5s' },
  starLow: { bottom: 2, right: 4, color: AD.decorTint, animationDelay: '1s' },
  starTiny: { bottom: 12, left: 12, color: AD.accent, animationDelay: '1.4s' },
  stageText: {
    fontFamily: AD.fontFamily,
    fontSize: 13,
    fontWeight: 700,
    color: AD.text,
    lineHeight: 1.35,
  },
  percent: {
    fontFamily: AD.fontFamily,
    fontSize: 11,
    fontWeight: 700,
    color: AD.cta,
    lineHeight: 1,
  },
  track: {
    width: '72%',
    maxWidth: 190,
    height: 5,
    borderRadius: AD.radiusPill,
    background: fade(AD.primary, 0.12),
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: AD.radiusPill,
    background: `linear-gradient(90deg, ${AD.decorSoft}, ${AD.cta})`,
    transition: 'width 600ms ease',
  },
  tile: { borderRadius: AD.radiusLg, flexShrink: 0 },
  footer: { padding: '11px 14px 14px' },
  statRow: { display: 'flex', alignItems: 'center', marginBottom: 10 },
  stat: { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
  statDivider: {
    width: 1,
    alignSelf: 'stretch',
    background: AD.border,
    margin: '0 8px',
  },
  button: { borderRadius: AD.radiusLg },
}));

const Star = ({ size = 18 }: { size?: number }): React.ReactElement => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0c.7 6.3 5 10.6 11.3 11.3v1.4C17 13.4 12.7 17.7 12 24c-.7-6.3-5-10.6-11.3-11.3v-1.4C7 10.6 11.3 6.3 12 0Z" />
  </svg>
);

const Stat = ({ classes }: { classes: ReturnType<typeof useStyles> }): React.ReactElement => (
  <Box className={classes.stat}>
    <Skeleton variant="rect" width={24} height={24} className={classes.tile} animation="wave" />
    <Box flex={1} minWidth={0}>
      <Skeleton variant="text" width="55%" height={12} animation="wave" />
      <Skeleton variant="text" width="80%" height={16} animation="wave" />
    </Box>
  </Box>
);

const AdVariantSkeleton = (props: Props): React.ReactElement => {
  const { progress } = props;
  const classes = useStyles();
  const reported = Math.min(100, Math.max(0, Number((progress || {}).percent) || 0));

  // The bar only ever moves forward, even if a reported step goes back.
  const high = useRef(reported);
  if (reported > high.current) high.current = reported;
  const percent = high.current;

  const stage = generationLabel(progress);

  return (
    <Card className={classes.root} aria-busy="true" aria-label="Generating ad">
      <Box className={classes.header}>
        <Skeleton variant="circle" width={22} height={22} animation="wave" />
        <Skeleton variant="text" width={92} height={14} animation="wave" />
      </Box>

      <Box className={classes.previewPane}>
        <Box className={classes.caption}>
          <Skeleton variant="text" width="94%" height={11} animation="wave" />
          <Skeleton variant="text" width="68%" height={11} animation="wave" />
        </Box>

        <Box className={classes.media}>
          <Box className={classes.sheen} />
          <Box className={classes.stage}>
            <Box className={classes.sparkles} aria-hidden="true">
              <span className={`${classes.star} ${classes.starLead}`}>
                <Star size={26} />
              </span>
              <span className={`${classes.star} ${classes.starUp}`}>
                <Star size={14} />
              </span>
              <span className={`${classes.star} ${classes.starLow}`}>
                <Star size={17} />
              </span>
              <span className={`${classes.star} ${classes.starTiny}`}>
                <Star size={9} />
              </span>
            </Box>
            <Typography className={classes.stageText}>{stage}</Typography>
            <Box
              className={classes.track}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={stage}
            >
              <Box className={classes.fill} style={{ width: `${percent}%` }} />
            </Box>
            <Typography className={classes.percent}>{percent}%</Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Skeleton
          variant="rect"
          width="100%"
          height={34}
          className={classes.button}
          animation="wave"
          style={{ marginBottom: 8 }}
        />
        <Box className={classes.statRow}>
          <Stat classes={classes} />
          <Box className={classes.statDivider} />
          <Stat classes={classes} />
        </Box>
        <Skeleton variant="rect" width="100%" height={34} className={classes.button} animation="wave" />
      </Box>
    </Card>
  );
};

interface Props {
  progress?: { stage?: string; percent?: number } | null;
}

export default React.memo(AdVariantSkeleton);
